/** GET `social-network-types` — product Instagram is this description only (never legacy `instagram`). */
export const INSTAGRAM_GRAPH_DESCRIPTION = "instagram_graph"

export function socialNetworkDescription(value) {
  if (value && typeof value === "object") {
    return String(value.description ?? "").trim().toLowerCase()
  }
  return String(value ?? "").trim().toLowerCase()
}

export function findSocialNetworkTypeById(socialNetworkTypes, typeId) {
  const id = Number(typeId)
  if (Number.isNaN(id)) return null
  return (Array.isArray(socialNetworkTypes) ? socialNetworkTypes : []).find(
    (type) => Number(type?.id) === id,
  ) ?? null
}

export function findSocialNetworkTypeByDescription(socialNetworkTypes, description) {
  const want = socialNetworkDescription(description)
  if (!want) return null
  return (Array.isArray(socialNetworkTypes) ? socialNetworkTypes : []).find(
    (type) => socialNetworkDescription(type) === want,
  ) ?? null
}

export function isInstagramGraphDescription(description) {
  return socialNetworkDescription(description) === INSTAGRAM_GRAPH_DESCRIPTION
}

/**
 * Dashboard / chart key from API `description` (exact).
 * `instagram_graph` → `instagram`. Legacy `instagram` is not a dashboard channel.
 */
export function dashboardPlatformKeyFromDescription(description) {
  const d = socialNetworkDescription(description)
  if (d === INSTAGRAM_GRAPH_DESCRIPTION) return "instagram"
  if (d === "facebook") return "facebook"
  if (d === "twitter") return "twitter"
  if (d === "linkedin") return "linkedin"
  if (d === "tiktok") return "tiktok"
  if (d === "all") return "all"
  return null
}

/** Planner / calendar UI channel id (`twitter` → `x`). */
export function plannerChannelIdFromDescription(description) {
  const key = dashboardPlatformKeyFromDescription(description)
  if (key === "twitter") return "x"
  return key
}

export function dashboardPlatformKeyFromType(type) {
  return dashboardPlatformKeyFromDescription(type)
}

export function socialNetworkTypeIdToDashboardPlatformKey(typeId, socialNetworkTypes = []) {
  const type = findSocialNetworkTypeById(socialNetworkTypes, typeId)
  return type ? dashboardPlatformKeyFromDescription(type) : null
}

export function plannerChannelIdFromTypeId(typeId, socialNetworkTypes = []) {
  const type = findSocialNetworkTypeById(socialNetworkTypes, typeId)
  return type ? plannerChannelIdFromDescription(type) : null
}

/** API description shown in UI: `instagram_graph` is named `instagram`. */
export function socialNetworkDisplayName(description) {
  const raw = description && typeof description === "object"
    ? String(description.description ?? "").trim()
    : String(description ?? "").trim()
  if (socialNetworkDescription(raw) === INSTAGRAM_GRAPH_DESCRIPTION) return "instagram"
  return raw
}

export function socialNetworkTypeIdToLabel(socialNetworkTypes, typeId) {
  const type = findSocialNetworkTypeById(socialNetworkTypes, typeId)
  if (!type) return ""
  return socialNetworkPrettyLabel(type)
}

/**
 * UI or API key → catalog id.
 * Dashboard key `instagram` means `instagram_graph` only (not legacy `instagram`).
 */
export function catalogTypeIdFromKey(socialNetworkTypes, key) {
  const raw = socialNetworkDescription(key)
  if (!raw) return null
  if (/^\d+$/.test(raw)) {
    const type = findSocialNetworkTypeById(socialNetworkTypes, raw)
    return type ? Number(type.id) : null
  }
  if (raw === "instagram" || raw === INSTAGRAM_GRAPH_DESCRIPTION) {
    const type = findSocialNetworkTypeByDescription(socialNetworkTypes, INSTAGRAM_GRAPH_DESCRIPTION)
    return type ? Number(type.id) : null
  }
  if (raw === "x") {
    const type = findSocialNetworkTypeByDescription(socialNetworkTypes, "twitter")
    return type ? Number(type.id) : null
  }
  const type = findSocialNetworkTypeByDescription(socialNetworkTypes, raw)
  return type ? Number(type.id) : null
}

/**
 * Compare an API `description` to a UI channel key.
 * UI `instagram` matches `instagram_graph` only.
 */
export function apiDescriptionMatchesUiKey(apiDescription, uiKey) {
  const apiKey = dashboardPlatformKeyFromDescription(apiDescription)
  const ui = socialNetworkDescription(uiKey)
  if (!apiKey || !ui) {
    const api = socialNetworkDescription(apiDescription)
    if (api && api === ui && api !== "instagram") return true
    return false
  }
  if (ui === "x") return apiKey === "twitter"
  if (ui === INSTAGRAM_GRAPH_DESCRIPTION) return apiKey === "instagram"
  return apiKey === ui
}

export function channelKeysEquivalent(left, right) {
  if (apiDescriptionMatchesUiKey(left, right)) return true
  if (apiDescriptionMatchesUiKey(right, left)) return true
  const a = socialNetworkDescription(left)
  const b = socialNetworkDescription(right)
  if (!a || !b) return false
  if (a === b && a !== "instagram") return true
  if ((a === "x" && b === "twitter") || (a === "twitter" && b === "x")) return true
  if (
    (a === "instagram" && b === INSTAGRAM_GRAPH_DESCRIPTION) ||
    (b === "instagram" && a === INSTAGRAM_GRAPH_DESCRIPTION)
  ) {
    return true
  }
  return false
}

/** Paid / ads `social_network` string → dashboard key. Legacy `instagram` is ignored. */
export function normalizePlatformKey(platform) {
  const d = socialNetworkDescription(platform)
  if (!d || d === "meta" || d === "mhs") return ""
  if (d === "x") return "twitter"
  const fromApi = dashboardPlatformKeyFromDescription(d)
  if (fromApi) return fromApi
  if (d === "instagram" || d === "facebook" || d === "linkedin" || d === "twitter" || d === "tiktok" || d === "all") {
    return d
  }
  return d
}

/** Social Performance table channels. Instagram is `instagram_graph` only. */
export const DASHBOARD_SOCIAL_PERFORMANCE_PLATFORM_KEYS = [
  "instagram",
  "facebook",
  "linkedin",
  "twitter",
  "tiktok",
]

export function socialNetworkPrettyLabel(description) {
  const d = socialNetworkDescription(description)
  if (d === INSTAGRAM_GRAPH_DESCRIPTION) return "Instagram"
  if (d === "facebook") return "Facebook"
  if (d === "twitter" || d === "x") return "X"
  if (d === "linkedin") return "LinkedIn"
  if (d === "tiktok") return "TikTok"
  if (d === "all") return "All Platforms"
  return ""
}

export function socialNetworkPrettyLabelForDashboardKey(key) {
  const d = socialNetworkDescription(key)
  if (d === "instagram") return "Instagram"
  return socialNetworkPrettyLabel(d)
}
